<template>
    <div>
        <!-- Hamburger Button -->
        <button
            type="button"
            class="mobile-nav-trigger"
            aria-label="Toggle mobile menu"
            @click="isOpen = true"
        >
            <Bars3Icon class="h-6 w-6" aria-hidden="true" />
        </button>

        <!-- Mobile Menu Dialog -->
        <Teleport to="body">
            <TransitionRoot :show="isOpen" as="template">
                <Dialog as="div" class="mobile-nav-dialog" @close="isOpen = false">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="mobile-nav-backdrop" />
                </TransitionChild>

                <div class="mobile-nav-container">
                    <TransitionChild
                        as="template"
                        enter="transform transition ease-in-out duration-300"
                        enter-from="translate-x-full"
                        enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-200"
                        leave-from="translate-x-0"
                        leave-to="translate-x-full"
                    >
                        <DialogPanel class="mobile-nav-panel">
                            <!-- Header -->
                            <div class="mobile-nav-header">
                                <DialogTitle class="mobile-nav-title">Menu</DialogTitle>
                                <button
                                    type="button"
                                    class="mobile-nav-close"
                                    aria-label="Close menu"
                                    @click="isOpen = false"
                                >
                                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>

                            <!-- Navigation Links -->
                            <nav class="mobile-nav-links">
                                <template v-for="item in navigationItems" :key="item.id">
                                    <router-link
                                        v-if="item.id.startsWith('__route:')"
                                        :to="item.id.replace('__route:', '')"
                                        class="mobile-nav-link"
                                        @click="isOpen = false"
                                    >
                                        {{ item.label }}
                                    </router-link>
                                    <a
                                        v-else
                                        :href="`#${item.id}`"
                                        class="mobile-nav-link"
                                        @click="handleNavClick(item.id)"
                                    >
                                        {{ item.label }}
                                    </a>
                                </template>
                            </nav>

                            <!-- Login Link -->
                            <div v-if="loginUrl" class="mobile-nav-login">
                                <a
                                    :href="loginUrl"
                                    target="_blank"
                                    rel="nofollow noopener noreferrer"
                                    class="mobile-nav-login-link"
                                    @click="isOpen = false"
                                >
                                    {{ $t('header.login') }}
                                    <ArrowTopRightOnSquareIcon class="h-4 w-4" aria-hidden="true" />
                                </a>
                            </div>

                            <!-- Language Picker -->
                            <div class="mobile-nav-footer">
                                <div class="mobile-language-buttons">
                                    <button
                                        :class="[
                                            'mobile-language-btn',
                                            currentLocale === 'en' ? 'active' : ''
                                        ]"
                                        @click="changeLocale('en')"
                                    >
                                        <span class="flag-icon">🇬🇧</span>
                                        English
                                    </button>
                                    <button
                                        :class="[
                                            'mobile-language-btn',
                                            currentLocale === 'lt' ? 'active' : ''
                                        ]"
                                        @click="changeLocale('lt')"
                                    >
                                        <span class="flag-icon">🇱🇹</span>
                                        Lietuvių
                                    </button>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
            </TransitionRoot>
        </Teleport>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, type PropType } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue';
import { Bars3Icon, XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import type { Locale } from '@/i18n';

interface NavigationItem {
    id: string;
    label: string;
}

export default defineComponent({
    name: 'MobileNav',
    components: {
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionRoot,
        TransitionChild,
        Bars3Icon,
        XMarkIcon,
        ArrowTopRightOnSquareIcon
    },
    props: {
        navigationItems: {
            type: Array as PropType<NavigationItem[]>,
            required: true
        },
        loginUrl: {
            type: String,
            default: ''
        }
    },
    emits: ['navigate'],
    setup(_props, { emit }) {
        const isOpen = ref(false);
        const { locale } = useI18n();
        const router = useRouter();
        const route = useRoute();
        const currentLocale = computed(() => locale.value as Locale);

        const handleNavClick = (id: string) => {
            isOpen.value = false;
            emit('navigate', id);
        };

        const changeLocale = async (newLocale: Locale) => {
            const newPath = `/${newLocale}`;
            const hash = route.hash;
            await router.push({ path: newPath, hash });
            locale.value = newLocale;
            isOpen.value = false; // Close the mobile menu after selection
        };

        return {
            isOpen,
            handleNavClick,
            currentLocale,
            changeLocale
        };
    }
});
</script>

<style scoped>
.mobile-nav-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    color: rgb(var(--text-primary));
    border-radius: 0.5rem;
    transition: all 0.2s ease;
}

.mobile-nav-trigger:hover {
    background-color: rgb(var(--bg-muted));
}

.mobile-nav-trigger:focus-visible {
    outline: none;
    ring: 2px solid rgb(var(--accent-500));
    ring-offset: 2px;
}

.mobile-nav-dialog {
    position: relative;
    z-index: 60;
}

.mobile-nav-backdrop {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    z-index: 60;
}

.mobile-nav-container {
    position: fixed;
    inset: 0;
    overflow: hidden;
    z-index: 60;
}

.mobile-nav-panel {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 100%;
    max-width: 20rem;
    background-color: rgb(var(--background));
    box-shadow: -10px 0 15px -3px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
}

.mobile-nav-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem;
    border-bottom: 1px solid rgb(var(--border) / 0.1);
}

.mobile-nav-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: rgb(var(--text-primary));
}

.mobile-nav-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    color: rgb(var(--text-primary));
    border-radius: 0.5rem;
    transition: all 0.2s ease;
}

.mobile-nav-close:hover {
    background-color: rgb(var(--bg-muted));
}

.mobile-nav-close:focus-visible {
    outline: none;
    ring: 2px solid rgb(var(--accent-500));
    ring-offset: 2px;
}

.mobile-nav-links {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    gap: 0.25rem;
    overflow-y: auto;
}

.mobile-nav-link {
    display: block;
    padding: 0.875rem 1rem;
    font-size: 1rem;
    font-weight: 500;
    color: rgb(var(--muted-foreground));
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    position: relative;
}

.mobile-nav-link::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 0;
    background-color: rgb(var(--accent));
    border-radius: 0 2px 2px 0;
    transition: height 0.2s ease;
}

.mobile-nav-link:hover {
    background-color: rgb(var(--accent) / 0.1);
    color: rgb(var(--accent));
    padding-left: 1.25rem;
}

.mobile-nav-link:hover::before {
    height: 70%;
}

.mobile-nav-link:active {
    background-color: rgb(var(--accent) / 0.2);
}

.mobile-nav-login {
    padding: 0.75rem 1rem;
    border-top: 1px solid rgb(var(--border) / 0.1);
}

.mobile-nav-login-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 1rem;
    font-size: 1rem;
    font-weight: 500;
    color: rgb(var(--muted-foreground));
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    text-decoration: none;
}

.mobile-nav-login-link:hover {
    background-color: rgb(var(--accent) / 0.1);
    color: rgb(var(--accent));
}

.mobile-nav-footer {
    padding: 1.5rem;
    border-top: 1px solid rgb(var(--border) / 0.1);
    display: flex;
    justify-content: center;
}

.mobile-language-buttons {
    display: flex;
    gap: 0.5rem;
    width: 100%;
}

.mobile-language-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    background-color: rgb(var(--bg-muted));
    color: rgb(var(--text-primary));
    border: 1px solid transparent;
    transition: all 0.2s ease;
}

.mobile-language-btn:hover {
    background-color: rgb(var(--accent) / 0.1);
}

.mobile-language-btn.active {
    background-color: rgb(var(--accent));
    color: rgb(var(--accent-foreground));
}

.flag-icon {
    font-size: 1.125rem;
}

/* Dark mode support */
.dark .mobile-nav-panel {
    background-color: rgb(var(--background));
}

.dark .mobile-nav-backdrop {
    background-color: rgba(0, 0, 0, 0.7);
}
</style>
